import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { initGA, trackPageview } from './utils/analytics';
import './index.css';

import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailPending from './pages/VerifyEmailPending';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import UploadData from './pages/UploadData';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import Billing from './pages/Billing';
import Integrations from './pages/Integrations';
import EmailScheduling from './pages/EmailScheduling';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminAgencies from './pages/SuperAdminAgencies';
import SuperAdminSubscriptions from './pages/SuperAdminSubscriptions';
import SuperAdminPricing from './pages/SuperAdminPricing';
import SuperAdminPayments from './pages/SuperAdminPayments';
import SuperAdminSettings from './pages/SuperAdminSettings';

const ENABLE_PLATFORM_SYNC = process.env.REACT_APP_ENABLE_PLATFORM_SYNC === 'true';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div className="spin" style={{ width: 32, height: 32, border: '3px solid #E2E8F0', borderTopColor: '#2563EB', borderRadius: '50%' }} />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Every route below this point is blocked server-side too (see
  // EMAIL_VERIFICATION_ALLOWLIST in middleware/auth.js) — this just avoids
  // rendering a dashboard shell that would immediately fail every API call.
  if (user.role !== 'super_admin' && user.emailVerified === false) return <VerifyEmailPending />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;

  if (!user) return children;

  return (
    <Navigate
      to={user.role === 'super_admin' ? '/super-admin' : '/dashboard'}
      replace
    />
  );
};

const RoleRoute = ({ allowedRoles, children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;

  if (!user) return <Navigate to="/login" replace />;

  if (!allowedRoles.includes(user.role)) {
    return (
      <Navigate
        to={user.role === 'super_admin' ? '/super-admin' : '/dashboard'}
        replace
      />
    );
  }

  return children;
};
// Fires a GA4 page_view on every client-side route change. GA's automatic
// pageview (from the base gtag config call) only fires once, at script load
// — it has no idea React Router just swapped the page without a real
// navigation, so this has to be explicit. Rendered inside BrowserRouter so
// useLocation is in scope.
const AnalyticsListener = () => {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
};

const HomeRedirect = () => {
  const { user } = useAuth();

  return (
    <Navigate
      to={user?.role === 'super_admin'
        ? '/super-admin'
        : '/dashboard'}
      replace
    />
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsListener />
        <Toaster
          position="top-right"
          toastOptions={{
            style: { fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, borderRadius: 10 },
            success: { iconTheme: { primary: '#059669', secondary: '#fff' } },
            error: { iconTheme: { primary: '#DC2626', secondary: '#fff' } },
          }}
        />
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
           <Route
             index
             element={
               <RoleRoute allowedRoles={['admin', 'super_admin']}>
                 <HomeRedirect />
               </RoleRoute>

             }
           />
           <Route
             path="super-admin/agencies"
             element={
               <RoleRoute allowedRoles={['super_admin']}>
                 <SuperAdminAgencies />
               </RoleRoute>
             }
           />

           <Route
             path="super-admin/subscriptions"
             element={
               <RoleRoute allowedRoles={['super_admin']}>
                 <SuperAdminSubscriptions />
               </RoleRoute>
             }
           />

           <Route
             path="super-admin/pricing"
             element={
               <RoleRoute allowedRoles={['super_admin']}>
                 <SuperAdminPricing />
               </RoleRoute>
             }
           />

           <Route
             path="super-admin/payments"
             element={
               <RoleRoute allowedRoles={['super_admin']}>
                 <SuperAdminPayments />
               </RoleRoute>
             }
           />

           <Route
             path="super-admin/settings"
             element={
               <RoleRoute allowedRoles={['super_admin']}>
                 <SuperAdminSettings />
               </RoleRoute>
             }
           />
           <Route
             path="dashboard"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <Dashboard />
               </RoleRoute>
             }
           />

           <Route
             path="clients"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <Clients />
               </RoleRoute>
             }
           />

           <Route
             path="clients/:id"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <ClientDetail />
               </RoleRoute>
             }
           />

           <Route
             path="upload"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <UploadData />
               </RoleRoute>
             }
           />

           <Route
             path="reports"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <Reports />
               </RoleRoute>
             }
           />

           <Route
             path="subscription"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <Subscription />
               </RoleRoute>
             }
           />

           <Route
             path="billing"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <Billing />
               </RoleRoute>
             }
           />

           <Route
             path="integrations"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 {ENABLE_PLATFORM_SYNC ? <Integrations /> : <Navigate to="/dashboard" replace />}
               </RoleRoute>
             }
           />

           <Route
             path="email-scheduling"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <EmailScheduling />
               </RoleRoute>
             }
           />

           <Route
             path="settings"
             element={
               <RoleRoute allowedRoles={['admin']}>
                 <Settings />
               </RoleRoute>
             }
           />

           <Route
             path="super-admin"
             element={
               <RoleRoute allowedRoles={['super_admin']}>
                 <SuperAdminDashboard />
               </RoleRoute>
             }
           />

          </Route>


          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
